<template>
  <div class="map-view">
    <div id="map" :loading="loading"></div>
  </div>
</template>

<script>
import icon from 'leaflet/dist/images/marker-icon.png'
import iconShadow from 'leaflet/dist/images/marker-shadow.png'

export default {
  name: 'MapView',
  props: {
    events: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
        map: {},
        markers: [],
        cEvents: [],
        loading: true,
    }
  },
  methods: {
    createMap() {
      let soonestMeet = this.events[0]
      let zoomLevel = 6
      this.map = L.map('map')

      this.createCustomButton('fa fa-long-arrow-left back-button', this.goToHomepage)
      this.reorderMapControlButtons()

      this.map.setView([soonestMeet.lat, soonestMeet.lng], zoomLevel)
      L.tileLayer('https://api.tiles.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}', {
          attribution: this.$store.getters.dataAttribution,
          tileSize: 512,
          maxZoom: 18,
          zoomOffset: -1,
          id: 'mapbox/streets-v11',
          accessToken: process.env.MAP_CRED
      }).addTo(this.map);
      this.addMarkers()
    },
    addMarkers(existingMap) {
      let event, marker, CustomIcon, newIcon, content,
          markerMap = {},
          removeHighlight = this.removeListItemHighlight

      this.markers = []
      for (let i in this.events) {
        event = this.events[i]
        newIcon = this.createCustomIcon({iconUrl: event.logo})
        content = this.getMarkerPopupContent(event)
        marker = L.marker([
          event.lat,
          event.lng
        ], {
          icon: newIcon
        }).addTo(this.map).bindPopup(content)

        marker.addEventListener('click', function(e) {
          // Marker structure must be traversed to get popup HTML for event ID
          let tr, p = e.target._popup._wrapper

          removeHighlight()

          // Find table row w/ same ID as marker then add class to highlight it
          if (getFirstChild(p) && getFirstChild(p.childNodes[0])) {
            p = getFirstChild(p.childNodes[0])
            if (p.getAttribute('data-id')) {
              tr = document.querySelector(`tr[data-id="${p.getAttribute('data-id')}"]`)
              tr.className += 'highlighted'
              scrollToListItem(tr)
            }
          }
        })
        // Add marker to mapping to create table row click event
        markerMap[event.id] = marker
        this.markers.push(marker)
      }
      /* Have to assign the marker mapping to global variable so that
          list component can use it */
      window.markerMap = markerMap

      // No need to add these buttons twice
      if (!existingMap) {
        // Add extra map marker if user allows to access location.
        this.getUserLocation()

        this.createCustomButton('fa fa-expand', this.expandMapView)

        this.createCustomButton('fa fa-filter', this.toggleModal)
      }

      let getFirstChild = function(n) {
        if (n.childNodes && n.childNodes.length) {
          return n.childNodes[0]
        }
        return []
      }
      let scrollToListItem = function(node) {
        let parentDiv = document.querySelector('div.list-view')
        parentDiv.scrollTop = node.offsetTop
      }
    },
    recreateMapMarkers() {
      // Clear existing markers from map then add new ones
      for (let marker of this.markers) {
        this.map.removeLayer(marker)
      }
      this.addMarkers(true)
    },
    removeListItemHighlight() {
      let listItems = document.querySelectorAll('table tr[data-id]')
      // Remove all highlighted classes
      for (let i in listItems) {
        if (listItems[i].className) {
          listItems[i].className = listItems[i].className.replace('highlighted', '')
        }
      }
    },
    createCustomIcon(data) {
      // https://stackoverflow.com/questions/171251/how-can-i-merge-properties-of-two-javascript-objects-dynamically
      return L.icon({
        ...data,
        ...{
          iconSize: [31, 41],
          iconAnchor: [15, 41],
          popupAnchor: [1, -41],
        },
      })
    },
    getMarkerPopupContent(event) {
      return `<h3 data-id="${event.id}">
      <a href="${event.url}" target="_blank">${event.title}</a></h3>
      <p><i class="fa fa-calendar"></i>&nbsp;<span>${event.startDate}</span> - <span>${event.endDate}</span></p>
      <hr />
      <p class="event-address">${event.location}</p>`
    },
    getUserLocation() {
      navigator.geolocation.getCurrentPosition(this.createUserMarker)
    },
    createUserMarker(locData) {
      if (locData) {
        this.map.userCoords = locData.coords
        let marker, coords = locData.coords
        // https://github.com/Leaflet/Leaflet/issues/4968
        let defaultIcon = this.createCustomIcon({
          iconUrl: '/static/usa-marker-small.png',
          shadowUrl: iconShadow,
        })
        L.Marker.prototype.options.icon = defaultIcon

        marker = L.marker([
          coords.latitude,
          coords.longitude
        ]).addTo(this.map).bindPopup(`<h4>My location</h4>`)
        marker.addEventListener('click', this.removeListItemHighlight)
        // Focus map on user's location
        this.focusUserLocation()
        // Create map button to focus on user's location
        this.createCustomButton('fa fa-bullseye', this.focusUserLocation)
      } else {
        console.error('Error retrieving location', locData)
      }
    },
    focusUserLocation() {
      // Remove list item highlight
      this.removeListItemHighlight()

      if (this.map.userCoords) {
        // Do this if the user has allowed to get their location.
        this.map.setView([this.map.userCoords.latitude, this.map.userCoords.longitude])
      }
    },
    createCustomButton(className, cb) {
      let customCtrl = L.Control.extend({
        options: {
          position: 'topleft',
        },
        onAdd: function(map) {
          let container = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-custom');
          container.appendChild(L.DomUtil.create('i', className))

          container.onclick = cb
          return container
        },
      })
      this.map.addControl(new customCtrl(cb))
    },
    expandMapView() {
      // All we need to do is toggle a class to the .list-view container
      let container = document.querySelector('.map-view')
      let sibling = document.querySelector('.list-view')
      if (container.classList.contains('full-screen')) {
        container.classList.remove('full-screen')
        sibling.classList.remove('hidden')
      } else {
        container.classList.add('full-screen')
        sibling.classList.add('hidden')
      }
      // Resize map dynamically since its container has changed size
      this.map.invalidateSize()
    },
    goToHomepage() {
      this.$router.push('/')
    },
    toggleModal() {
      this.$store.commit('toggleShowFilterModal')
    },
    reorderMapControlButtons() {
      let zoomButtonsContainer = document.querySelector('.leaflet-control-zoom')
      let backButtonContainer = document.querySelector('.back-button').parentElement
      // Move back button above zoom buttons
      zoomButtonsContainer.parentElement.prepend(backButtonContainer)
    }
  },
  watch: {
    events: function(eventsArray) {
      if (this.cEvents !== eventsArray && !this.loading) {
        // Assume that if `cEvents` has changed & loading complete, filters were added
        this.recreateMapMarkers()
        this.cEvents = eventsArray
      } else if (eventsArray && eventsArray.length && this.loading) {
        this.createMap()
        this.loading = false
        this.cEvents = eventsArray
      }
    },
  },
}
</script>

<style lang="scss">
%map-container-size {
  width: 100%;
}
// All parents of #map have to use same 100% height + width :-(
html, body, #app, .split-view, #map {
  width: 100%;
  height: 100%;
}
.map-view {
  @extend %map-container-size;
}

#map {
  .event-address {
    font-weight: bold;
    font-size: 1.1em;
  }
  .leaflet-popup-content {
    h3 {
      font-size: 1.1rem;
    }
    h4 {
      font-size: 1rem;
    }
    p {
      margin: 4px 0;
    }
    hr {
      margin: 0.5rem 0;
    }
  }
  img.leaflet-marker-icon {
    border-radius: 1em;
  }
  // .leaflet-popup-tip-container {
  //   margin-left: -5px;
  // }
  .leaflet-control-container {
    .leaflet-control-custom {
      line-height: 40px;
      width: 34px;
      height: 34px;
      background-color: #fff;
      &:hover {
        background-color: #f4f4f4;
        cursor: pointer;
      }
      .fa-bullseye, .fa-expand, .fa-long-arrow-left, .fa-filter {
        width: 100%;
        height: 100%;
        text-align: center;
        &::before {
          font-size: 24px;
        }
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Map.vue